import ActionButton from "@/components/buttons/ActionButton";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import FileTypeIconWithFilename from "@/components/icons/FileTypeIconWithFilename";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { UploadDragger } from "@/components/upload-dragger/UploadDragger";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { RcFile } from "antd/lib/upload";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useDeleteAgentAttachment,
  useDownloadAgentAttachmentsAsZip,
  useUploadAgentAttachments,
} from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import { AGENT_ROUTE_PATHS } from "../../paths";
import { Agent, AgentAttachment } from "../../types";
import styles from "./AgentAttachmentsTable.module.scss";

interface Props {
  agent?: Agent;
  hasUpload?: boolean;
  hasDelete?: boolean;
}

export const AgentAttachmentsTable = ({ agent, hasDelete, hasUpload }: Props) => {
  const { t } = useTranslation();

  const uploadAgentAttachments = useUploadAgentAttachments();
  const downloadAgentAttachmentsAsZip = useDownloadAgentAttachmentsAsZip();
  const deleteAgentAttachment = useDeleteAgentAttachment();

  const handleAttachmentsUpload = (_: RcFile, fileList: RcFile[], agent: Agent): boolean => {
    if (fileList.length > 0) {
      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      fileList.splice(0, fileList.length);

      uploadAgentAttachments.mutate({ id: agent.id, object: formData });
    }

    return false;
  };

  const getColumns = (agent: Agent): ColumnsType<AgentAttachment> => [
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 160,
      render: (_, record) => formatLocaleDateTime(record.createdAt),
    },
    {
      key: "filename",
      title: t("common.filename"),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <FileTypeIconWithFilename contentType={record.file.contentType} filename={record.file.filename} ellipsis />
      ),
    },
    {
      key: "size",
      title: t("common.filesize"),
      width: 100,
      render: (_, record) => formatFileSize(record.file.size),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={generatePath(AGENT_ROUTE_PATHS.attachment.to, { id1: agent.id, id2: record.id })}
            target="_blank"
            icon="export"
            color="blue"
            text={t("common.open")}
          />

          {hasDelete && (
            <>
              <Divider orientation="vertical" />

              <Popconfirm
                title={t("common.deleteAttachmentConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => deleteAgentAttachment.mutate({ id1: agent.id, id2: record.id })}
              >
                <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
              </Popconfirm>
            </>
          )}
        </>
      ),
    },
  ];

  return agent ? (
    <>
      {agent.attachments.length > 0 && (
        <div className={styles.tableHeaderDownloadActions}>
          <ActionButton icon="download" onClick={() => downloadAgentAttachmentsAsZip.mutate({ id: agent.id })}>
            {t("agent.actions.downloadAttachmentsAsZip")}
          </ActionButton>
        </div>
      )}
      <Table<AgentAttachment>
        {...tableStandardProps()}
        columns={getColumns(agent)}
        scroll={{ x: TableSizes.SMALL }}
        dataSource={agent.attachments}
        pagination={{ ...paginationTableProps, pageSize: PageSizes.LARGE, total: agent.attachments.length }}
      />

      {hasUpload && (
        <div className="margin-top-medium margin-bottom-small">
          <UploadDragger
            multiple
            showUploadList={false}
            beforeUpload={(...params) => handleAttachmentsUpload(...params, agent)}
          />
        </div>
      )}
    </>
  ) : null;
};
